import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { z } from "zod";
import SpinnerSmall from "../SpinnerSmall";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { useUpdateIntro } from "@/tanstack-hooks";
import { useToast } from "@/hooks/use-toast";
import { CheckIcon, EraserIcon, PencilLineIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "@/hooks/useTranslation";

const introFormSchema = z.object({
  content: z
    .string()
    .max(120, "Message cannot exceed 120 characters")
    .optional(),
});

type IntroFormSchema = z.infer<typeof introFormSchema>;

export default function IntroConfigCard({
  currentIntro,
}: {
  currentIntro: string | null;
}) {
  const { toast } = useToast();
  const updateIntroMutation = useUpdateIntro();
  const [isSaved, setIsSaved] = useState(false);
  const t = useTranslation();

  const form = useForm<IntroFormSchema>({
    resolver: zodResolver(introFormSchema),
    defaultValues: {
      content: currentIntro || "",
    },
  });

  const handleUpdateIntro = (values: IntroFormSchema) => {
    updateIntroMutation.mutate(
      {
        intro: values.content || null,
      },
      {
        onSuccess: () => {
          form.reset({ content: values.content });
          setIsSaved(true);
          setTimeout(() => setIsSaved(false), 1000);
        },
        onError: () => {
          toast({
            variant: "destructive",
            title: t.MESSAGE_SHORTCUT.ERROR.UPDATE,
            description: t.SHARED.ERRORS.PERSISTENT,
            className: "dark",
          });
        },
      }
    );
  };

  return (
    <Card className="dark px-2 max-w-2xl w-full">
      <CardHeader className="space-y-4 px-4 sm:px-6">
        <CardTitle className="flex flex-row items-center">
          <PencilLineIcon className="h-5 w-5 mr-2" />
          {t.MESSAGE_SHORTCUT.LABELS.TITLE}
        </CardTitle>
        <CardDescription>
          {t.MESSAGE_SHORTCUT.LABELS.DESCRIPTION}
        </CardDescription>
      </CardHeader>
      <CardContent className="px-4 sm:px-6">
        <Form {...form}>
          <form
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleUpdateIntro)}
          >
            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Textarea
                      placeholder={t.MESSAGE_SHORTCUT.LABELS.INPUT_PLACEHOLDER}
                      className="resize-none min-h-24 rounded-xl text-base"
                      maxLength={120}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="mt-1" />
                </FormItem>
              )}
            />
            <div className="flex flex-row mt-4 justify-between items-center">
              <Button
                type="button"
                variant="secondary"
                disabled={form.watch("content")?.length === 0}
                onClick={() => form.setValue("content", "")}
              >
                <EraserIcon className="h-4 w-4 mr-2" />
                {t.MESSAGE_SHORTCUT.LABELS.CLEAR_BUTTON}
              </Button>
              <Button
                type="submit"
                disabled={
                  updateIntroMutation.isPending ||
                  form.getValues().content === currentIntro ||
                  (form.getValues().content === "" && currentIntro === null)
                }
              >
                {updateIntroMutation.isPending ? (
                  <SpinnerSmall black={true} />
                ) : isSaved ? (
                  <CheckIcon className="h-4 w-4" />
                ) : (
                  t.SHARED.LABELS.SAVE_BUTTON
                )}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
